import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Alert,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { FirebaseContext } from '../../components/Firebase';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore } from '../../store/organization.store';
import { useToast } from '../../contexts/ToastContext';
import * as ROUTES from '../../constants/routes';
import VaultLogo from '../../components/global/VaultLogo';

interface LicenseInfo {
  license: {
    id: string;
    jobTitle?: string;
    dateExpires?: number;
    dateStart?: number;
    licenseType?: string;
    client?: { label: string; value: string };
    agency?: { label: string; value: string };
    status: string;
  };
  track: {
    id: string;
    trackName: string;
    tags?: string[];
    genres?: string[];
  } | null;
  sourceOrg: {
    id: string;
    name: string;
  } | null;
  trackId: string;
  orgId: string;
  licenseId: string;
}

const ClaimLicense: React.FC = () => {
  const { claimCode } = useParams<{ claimCode: string }>();
  const history = useHistory();
  const firebase = useContext(FirebaseContext);
  const auth = useAuthStore(state => state.auth);
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);
  const [noOrg, setNoOrg] = useState(false);

  const organizations = useOrganizationStore(state => state.organizations);
  const currentOrgId = useOrganizationStore(state => state.currentOrgId);
  const currentOrgName = useOrganizationStore(state => state.currentOrgName);

  // Validate the claim code on mount and when auth changes
  useEffect(() => {
    const validateClaimCode = async () => {
      if (!firebase || !claimCode) {
        setError('Invalid claim link');
        setLoading(false);
        return;
      }

      try {
        const validation = await firebase.validateClaimCode(claimCode);

        if (!validation.valid) {
          setError(validation.error || 'Invalid claim code');
          setLoading(false);
          return;
        }

        setLicenseInfo(validation.licenseInfo as LicenseInfo);

        // Check if user is authenticated but has no org
        if (auth && organizations.length === 0) {
          setNoOrg(true);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to validate claim code');
      } finally {
        setLoading(false);
      }
    };

    validateClaimCode();
  }, [firebase, claimCode, auth, organizations]);

  // Handle claiming the license
  const handleClaimLicense = async () => {
    if (!firebase || !auth || !licenseInfo || !currentOrgId) return;

    setClaiming(true);
    setError(null);

    try {
      const success = await firebase.claimLicense(
        licenseInfo.orgId,
        licenseInfo.trackId,
        licenseInfo.licenseId,
        currentOrgId,
        auth.uid,
        currentOrgName || undefined
      );

      if (success) {
        showSuccess(`Successfully claimed license for "${licenseInfo.track?.trackName || 'track'}"!`);
        // Redirect to the library
        history.push(ROUTES.FIND_AUDIO);
      } else {
        throw new Error('Failed to claim license');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to claim license');
      showError('Failed to claim license');
      setClaiming(false);
    }
  };

  // Handle redirect to sign in (preserving claim code)
  const handleSignIn = () => {
    sessionStorage.setItem('pendingClaimCode', claimCode);
    history.push(ROUTES.SIGN_IN);
  };

  // Handle redirect to sign up (preserving claim code)
  const handleSignUp = () => {
    sessionStorage.setItem('pendingClaimCode', claimCode);
    history.push(ROUTES.SIGN_UP);
  };

  // Handle redirect to create org
  const handleCreateOrg = () => {
    sessionStorage.setItem('pendingClaimCode', claimCode);
    history.push(ROUTES.GET_STARTED);
  };

  // Handle redirect to select org
  const handleSelectOrg = () => {
    sessionStorage.setItem('pendingClaimCode', claimCode);
    history.push(ROUTES.SELECT_ORGANIZATION);
  };

  // Format license type for display
  const formatLicenseType = (licenseType?: string): string => {
    if (!licenseType || licenseType === 'none') return 'Not specified';
    if (licenseType === 'blanket') return 'Blanket (Perpetual)';
    if (licenseType === 'custom') return 'Custom';
    // Format like "3months" -> "3 Months"
    const match = licenseType.match(/^(\d+)(months|years)$/);
    if (match) {
      return `${match[1]} ${match[2].charAt(0).toUpperCase() + match[2].slice(1)}`;
    }
    return licenseType;
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <VaultLogo />
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner color="primary" />
            <p className="text-white mt-3">Validating claim code...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <VaultLogo />
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <CardBody className="text-center">
                <CardTitle tag="h3" className="text-white mb-4">
                  Invalid Claim Link
                </CardTitle>
                <Alert color="danger">{error}</Alert>
                <p className="text-muted">
                  This claim link may be invalid, expired, or already used.
                </p>
                <Button color="primary" onClick={() => history.push(ROUTES.SIGN_IN)}>
                  Go to Sign In
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!licenseInfo) {
    return null;
  }

  const { license, track, sourceOrg } = licenseInfo;

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card>
            <CardBody className="text-center">
              <CardTitle tag="h3" className="text-white mb-4">
                <i className="tim-icons icon-notes mr-2"></i>
                License Shared With You
              </CardTitle>

              {/* License Details */}
              <div className="mb-4 p-3" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                <p className="text-muted mb-1 small">From</p>
                <h5 className="text-white mb-3">{sourceOrg?.name || 'Unknown Organization'}</h5>

                <p className="text-muted mb-1 small">Track</p>
                <h4 className="text-white mb-3">{track?.trackName || 'Unknown Track'}</h4>

                {license.jobTitle && (
                  <>
                    <p className="text-muted mb-1 small">Campaign/Project</p>
                    <p className="text-white mb-3">{license.jobTitle}</p>
                  </>
                )}

                <div className="d-flex justify-content-around mt-3">
                  <div>
                    <p className="text-muted mb-1 small">License Type</p>
                    <p className="text-white mb-0">{formatLicenseType(license.licenseType)}</p>
                  </div>
                  {license.dateExpires && license.licenseType !== 'blanket' && (
                    <div>
                      <p className="text-muted mb-1 small">Expires</p>
                      <p className="text-white mb-0">{moment(license.dateExpires).format('DD/MM/YYYY')}</p>
                    </div>
                  )}
                </div>
              </div>

              <hr className="my-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              {/* Action Section */}
              {!auth ? (
                // Not signed in
                <>
                  <p className="text-muted mb-4">
                    Sign in or create an account to claim this license.
                  </p>
                  <div className="d-flex flex-column align-items-center gap-2">
                    <Button
                      className="btn-info-custom"
                      size="lg"
                      onClick={handleSignIn}
                      style={{ position: 'relative', zIndex: 2, minWidth: '180px' }}
                    >
                      <span style={{ position: 'relative', zIndex: 3 }}>Sign In</span>
                    </Button>
                    <Button color="outline-light" onClick={handleSignUp}>
                      Create Account
                    </Button>
                  </div>
                </>
              ) : noOrg || organizations.length === 0 ? (
                // Signed in but no organization
                <>
                  <Alert color="warning">
                    You need an organization to claim this license.
                  </Alert>
                  <p className="text-muted mb-4">
                    Create or join an organization to receive licensed assets.
                  </p>
                  <div className="d-flex flex-column align-items-center gap-2">
                    <Button
                      className="btn-primary-custom"
                      size="lg"
                      onClick={handleCreateOrg}
                      style={{ position: 'relative', zIndex: 2, minWidth: '180px' }}
                    >
                      <span style={{ position: 'relative', zIndex: 3 }}>Create Organization</span>
                    </Button>
                  </div>
                </>
              ) : !currentOrgId ? (
                // Signed in, has orgs, but no current org selected
                <>
                  <Alert color="info">
                    Select an organization to claim this license.
                  </Alert>
                  <p className="text-muted mb-4">
                    Choose which organization should receive this license.
                  </p>
                  <Button
                    className="btn-primary-custom"
                    size="lg"
                    onClick={handleSelectOrg}
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    <span style={{ position: 'relative', zIndex: 3 }}>Select Organization</span>
                  </Button>
                </>
              ) : (
                // Signed in with current org - can claim
                <>
                  <p className="text-muted mb-2">Claiming as</p>
                  <h5 className="text-white mb-4">{currentOrgName}</h5>

                  {organizations.length > 1 && (
                    <Button
                      color="link"
                      size="sm"
                      className="mb-3 text-muted"
                      onClick={handleSelectOrg}
                    >
                      Switch Organization
                    </Button>
                  )}

                  <Button
                    className="btn-success-custom"
                    size="lg"
                    onClick={handleClaimLicense}
                    disabled={claiming}
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    <span style={{ position: 'relative', zIndex: 3 }}>
                      {claiming ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Claiming...
                        </>
                      ) : (
                        'Claim License'
                      )}
                    </span>
                  </Button>
                </>
              )}

              <hr className="my-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              <p className="text-muted small mb-0">
                Claim Code: {claimCode}
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ClaimLicense;
